import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import prettyBytes from '../../../../utils/prettyBytes';
import Icon from '../../../general/Icon';
import Loader from '../../../general/Loader';
import Tooltip from '../../../utils/Tooltip';
import Text from '../../../typography/Text';
import FILE_TYPES from '../FILE_TYPES';
import FILE_EXTENSIONS from '../FILE_EXTENSIONS';
import { checkBrowserCanRender } from '../utils';
import { useTheme } from '../../../../utils/styles';
var _ref2 = /*#__PURE__*/React.createElement(Loader, {
  size: "tiny"
});
var _ref4 = /*#__PURE__*/React.createElement(Icon, {
  name: "crop"
});
var _ref5 = /*#__PURE__*/React.createElement(Icon, {
  name: "fileDownload"
});
var _ref6 = /*#__PURE__*/React.createElement(Icon, {
  name: "close"
});
function File(_ref) {
  let {
    classes,
    index,
    id,
    file,
    loading,
    progress,
    error,
    isUrl,
    preview,
    crop,
    cropTooltip,
    onRemove,
    onCrop,
    overrides,
    handleDownload,
    downloadTooltip,
    deleteTooltip,
    ...props
  } = _ref;
  const theme = useTheme();
  const canRemove = useMemo(() => {
    if (typeof (file === null || file === void 0 ? void 0 : file.canRemove) === 'boolean' && !file.canRemove) return false;
    return true;
  }, [file]);
  const handleOnRemove = useCallback(() => {
    if (!canRemove) return;
    if (id) {
      onRemove({
        id,
        file
      });
    } else {
      onRemove(file);
    }
  }, [file, onRemove, id, canRemove]);
  const handleOnCrop = useCallback(() => {
    if (isUrl) {
      fetch(file).then(res => res.blob()).then(blob => {
        blob.name = file.split('/').pop();
        onCrop(blob, index, id);
      }).catch(console.error);
    } else {
      onCrop(file, index, id);
    }
  }, [file, index, id, isUrl, onCrop]);
  const renderIcon = useMemo(() => {
    if (error) return /*#__PURE__*/React.createElement(Icon, {
      name: "errorOutline",
      color: theme.colors.red500
    });
    if (loading) return _ref2;
    if (preview && checkBrowserCanRender(isUrl ? file : file.name)) return /*#__PURE__*/React.createElement("span", {
      style: {
        backgroundImage: "url(\"".concat(isUrl ? file : URL.createObjectURL(file), "\")")
      }
    });
    return /*#__PURE__*/React.createElement(Icon, {
      name: FILE_TYPES[file.type] || FILE_EXTENSIONS[file.type] || 'file'
    });
  }, [error, theme.colors.red500, loading, preview, isUrl, file]);
  var _ref3 = /*#__PURE__*/React.createElement("span", null, cropTooltip);
  const renderCrop = useMemo(() => {
    if (!crop) return null;
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: _ref3
    }, /*#__PURE__*/React.createElement("span", {
      onClick: handleOnCrop,
      className: classes.crop
    }, _ref4));
  }, [crop, cropTooltip, handleOnCrop, classes.crop]);
  const fileClassName = classnames(classes.file, {
    [classes.fileError]: error,
    [classes.fileLoading]: loading
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: fileClassName
  }, overrides.file), /*#__PURE__*/React.createElement("div", {
    className: classes.fileDataContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileIconContainer
  }, renderIcon), /*#__PURE__*/React.createElement("div", {
    className: classes.fileTextContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileNameContainer
  }, /*#__PURE__*/React.createElement(Text, {
    className: classes.fileName,
    type: "caption",
    isTruncated: true,
    useTooltip: true
  }, isUrl ? file.split('/').pop() : file.name)), !error && !isUrl && (file === null || file === void 0 ? void 0 : file.size) > 0 && /*#__PURE__*/React.createElement(Text, {
    className: classes.fileSize,
    type: "caption"
  }, "(", prettyBytes(parseInt(file.size, 10)), ")"), error && error.length && /*#__PURE__*/React.createElement(Text, {
    className: classes.fileErrorText,
    type: "caption"
  }, error))), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, renderCrop, !loading && handleDownload && /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: /*#__PURE__*/React.createElement("span", null, downloadTooltip)
  }, /*#__PURE__*/React.createElement("span", {
    onClick: handleDownload,
    className: classes.clear
  }, _ref5)), !loading && canRemove && /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: /*#__PURE__*/React.createElement("span", null, deleteTooltip)
  }, /*#__PURE__*/React.createElement("span", {
    onClick: handleOnRemove,
    className: classes.clear
  }, _ref6))), !!progress && /*#__PURE__*/React.createElement("span", {
    className: classes.progress,
    style: {
      width: "".concat(progress, "%")
    }
  }));
}
File.defaultProps = {};
process.env.NODE_ENV !== "production" ? File.propTypes = {
  classes: PropTypes.object,
  className: PropTypes.string,
  index: PropTypes.number,
  onRemove: PropTypes.func,
  overrides: PropTypes.object
} : void 0;
export default /*#__PURE__*/React.memo(File);